import { invalidatePaymentUploadConfig } from './file-upload-api';
import keycloakApi from './keycloak-auth';

export interface ClientData {
  id: string;
  name: string;
  client_code: string | null;
  company_email: string | null;
  status: string;
  created_at: Date;
  updated_at: Date;
  primary_contact_name?: string | null;
  primary_contact_phone?: string | null;
}

export interface ClientsPaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ClientsResponse {
  data: ClientData[];
  pagination: ClientsPaginationData;
}

/**
 * Column keys `GET /api/clients` will sort by. Mirrors `queryClientSchema.sortBy`
 * server-side, which rejects anything else with a 400.
 */
export const CLIENT_SORT_BY_VALUES = [
  'id',
  'name',
  'client_code',
  'company_email',
  'primary_contact_name',
  'primary_contact_phone',
  'status',
  'created_at',
  'updated_at',
] as const;

export type ClientSortBy = (typeof CLIENT_SORT_BY_VALUES)[number];

export const isClientSortBy = (value: string): value is ClientSortBy =>
  (CLIENT_SORT_BY_VALUES as readonly string[]).includes(value);

export interface ClientFilters {
  page?: number;
  limit?: number;
  sortBy?: ClientSortBy;
  order?: 'asc' | 'desc';
  search?: string;
  status?: string; // single value per backend schema
}

const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return keycloakApi.authenticatedApiCall<T>(endpoint, options);
};

export const getClients = async (filters: ClientFilters = {}): Promise<ClientsResponse> => {
  const searchParams = new URLSearchParams();

  if (filters.page) searchParams.append('page', filters.page.toString());
  if (filters.limit) searchParams.append('limit', filters.limit.toString());
  if (filters.sortBy) searchParams.append('sortBy', filters.sortBy);
  // Backend expects sortOrder in 'ASC' | 'DESC'
  if (filters.order) searchParams.append('sortOrder', filters.order.toUpperCase());
  if (filters.search) searchParams.append('search', filters.search);
  if (filters.status) searchParams.append('status', filters.status);

  const queryString = searchParams.toString();
  const endpoint = `/clients${queryString ? `?${queryString}` : ''}`;

  return apiCall<ClientsResponse>(endpoint);
};

export const getClientById = async (id: string): Promise<ClientData> => {
  return apiCall<ClientData>(`/clients/${id}`);
};

export interface CreateClientData {
  name: string;
  client_code: string;
  company_email?: string | null;
  keycloak_group?: string | null;
}

export const createClient = async (data: CreateClientData): Promise<ClientData> => {
  return apiCall<ClientData>('/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export type {
  NotificationEventType,
  NotificationPreferencesResponse,
  UpdateEmailPreferencesRequest,
} from './notification-preferences';

export const getClientNotificationPreferences = async (
  clientId: string
): Promise<import('./notification-preferences').NotificationPreferencesResponse> => {
  return apiCall(`/clients/${clientId}/notifications`, { method: 'GET' });
};

export const updateClientEmailPreferences = async (
  clientId: string,
  data: import('./notification-preferences').UpdateEmailPreferencesRequest
): Promise<import('./notification-preferences').EmailNotificationPreferences> => {
  return apiCall(`/clients/${clientId}/notifications/email`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

export const removeClientEmailPreferences = async (
  clientId: string,
  emailAddress: string
): Promise<{ success: boolean; deletedCount: number }> => {
  return apiCall(`/clients/${clientId}/notifications/email`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emailAddress }),
  });
};

export const getClientSupplierEmailToggle = async (
  clientId: string
): Promise<{ enabled: boolean }> => {
  return apiCall(`/clients/${clientId}/supplier-emails-toggle`, { method: 'GET' });
};

export const updateClientSupplierEmailToggle = async (
  clientId: string,
  enabled: boolean
): Promise<{ enabled: boolean }> => {
  return apiCall(`/clients/${clientId}/supplier-emails-toggle`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
};

export interface ClientPaymentUploadConfig {
  allowedUploadTypes: Array<'ui_batch' | 'sftp'>;
  supportedUploadTypes: {
    uiBatch: boolean;
    sftp: boolean;
  };
}

export const getClientPaymentUploadTypes = async (
  clientId: string
): Promise<ClientPaymentUploadConfig> => {
  return apiCall<ClientPaymentUploadConfig>(`/clients/${clientId}/payment-upload-types`, {
    method: 'GET',
  });
};

export const updateClientPaymentUploadTypes = async (
  clientId: string,
  allowedUploadTypes: Array<'ui_batch' | 'sftp'>
): Promise<ClientPaymentUploadConfig> => {
  const result = await apiCall<ClientPaymentUploadConfig>(
    `/clients/${clientId}/payment-upload-types`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ allowedUploadTypes }),
    }
  );
  // The upload channels just changed, so drop the cached upload-config for this
  // client to avoid serving stale data to the payment files page.
  invalidatePaymentUploadConfig(clientId);
  return result;
};

export const uploadClientLogo = async (
  clientId: string,
  file: File
): Promise<{ logoUrl: string }> => {
  const formData = new FormData();
  formData.append('logo', file);
  return apiCall<{ logoUrl: string }>(`/clients/${clientId}/logo`, {
    method: 'POST',
    body: formData,
  });
};

export const deleteClientLogo = async (clientId: string): Promise<void> => {
  await apiCall<void>(`/clients/${clientId}/logo`, {
    method: 'DELETE',
  });
};

export const getClientLogoUrl = async (clientId: string): Promise<{ logoUrl: string | null }> => {
  return apiCall<{ logoUrl: string | null }>(`/clients/${clientId}/logo`, {
    method: 'GET',
  });
};
